<template>
  <div>
    <form @submit.prevent="submit" v-if="!verifyPersonError">
      <h2 class="form__title">Регистрация <span class="form__title-part-separated">Шаг 1 из 6</span></h2>
      <h3 class="form__subtitle form__subtitle_big-margin">Персональные данные</h3>
      <div class="form__description form__description_align-center">
        <p>
          Заполните все поля анкеты. Поля с&nbsp;*– обязательные для&nbsp;заполнения
        </p>
      </div>

      <div class="form-row form-row_2-cols form-row_mobile-no-flex form-row_multiline">
        <text-input
          label="Фамилия*"
          name="last-name"
          tabindex="1"
          v-model="$v.form.lastName.$model"
          :error="$v.form.lastName.$error && $v.form.email.$dirty"
          style="order: 1"
          excluded-symbols="А-яёЁ\s-"
        >
          <template #errors>
            <div class="text-input__error" v-if="$v.form.lastName.$error && $v.form.email.$dirty">
              <div v-if="!$v.form.lastName.required">Заполните поле</div>
              <div v-else-if="!$v.form.email.isValidName">
                Неверный формат фамилии
              </div>
            </div>
          </template>
        </text-input>

        <text-input
          label="Имя*"
          name="first-name"
          tabindex="2"
          v-model="$v.form.firstName.$model"
          :error="$v.form.firstName.$error && $v.form.email.$dirty"
          style="order: 3"
          excluded-symbols="А-яёЁ\s-"
        >
          <template #errors>
            <div class="text-input__error" v-if="$v.form.firstName.$error && $v.form.email.$dirty">
              <div v-if="!$v.form.firstName.required">Заполните поле</div>
              <div v-else-if="!$v.form.email.isValidName">
                Неверный формат имени
              </div>
            </div>
          </template>
        </text-input>

        <text-input
          label="Отчество*"
          name="middleName"
          tabindex="3"
          v-model="$v.form.middleName.$model"
          :error="$v.form.middleName.$error && $v.form.email.$dirty"
          style="order: 5"
          excluded-symbols="А-яёЁ\s-"
        >
          <template #errors>
            <div class="text-input__error" v-if="$v.form.middleName.$error && $v.form.email.$dirty">
              <div v-if="!$v.form.middleName.required">Заполните поле</div>
              <div v-else-if="!$v.form.email.isValidName">
                Неверный формат отчества
              </div>
            </div>
          </template>
        </text-input>

        <div class="text-input text-input_date"
          :class="{
            'text-input_filled': form.birthDate.length,
            'text-input_focused': dateFocus,
            'text-input_invalid': $v.form.birthDate.$error
          }"
          style="order: 2"
        >
          <label class="text-input__label"> Дата рождения* </label>
          <date-picker
            v-model="form.birthDate"
            format="DD-MM-YYYY"
            valueType="DD-MM-YYYY"
            type="date"
            placeholder="дд.мм.гггг"
            :clearable="false"
            :default-value="adultBirthDateInit"
            :disabled-date="adultBirthDate"
            :popup-class="birthDatePopupClassname"
            @open="dateFocus = true"
            @close="dateFocus = false"
          >
            <template #input>
              <input
                v-model="form.birthDate"
                type="text"
                class="text-input__field _date"
                v-mask="'##-##-####'"
                :tabindex="isMobile ? 4 : 5"
                @input="validateBirthDate"
              >
            </template>
          </date-picker>
          <div class="text-input__error" v-if="$v.form.birthDate.$error || birthDateInvalid">
            <div v-if="!$v.form.birthDate.required">Заполните поле</div>
            <div v-else-if="!$v.form.birthDate.availableDate || birthDateInvalid">Некорректная дата</div>
          </div>
        </div>

        <text-input
          label="Телефон*"
          type="tel"
          name="phone"
          :tabindex="isMobile ? 5 : 6"
          v-model="$v.form.mobilePhone.$model"
          :error="$v.form.mobilePhone.$error"
          style="order: 4"
        >
          <template #errors>
            <div class="text-input__error" v-if="$v.form.mobilePhone.$error">
              <div v-if="!$v.form.mobilePhone.required">Заполните поле</div>
              <div v-if="!$v.form.mobilePhone.minLength">Недостаточное количество символов</div>
            </div>
          </template>
        </text-input>

        <text-input
          label="E-mail*"
          type="text"
          name="email"
          :tabindex="isMobile ? 6 : 7"
          v-model="$v.form.email.$model"
          :error="$v.form.email.$error"
          style="order: 6"
        >
          <template #errors>
            <div class="text-input__error" v-if="$v.form.email.$error">
              <div v-if="!$v.form.email.required">
                Заполните поле
              </div>
              <div v-else-if="!$v.form.email.isValidUnicodeEmail">
                Неверный формат электронной почты
              </div>
              <div v-else-if="error">
                Эта электронная почта уже используется. Укажите другой адрес электронной почты.
              </div>
            </div>
          </template>
        </text-input>

        <dadata-custom
          label="Регион*"
          v-model="form.region"
          :error="$v.form.region.$error"
          from-bound="region"
          to-bound="region"
          style="order: 7"
          @get-suggestion="handleRegionDadataSuggestion"
        >
          <template #errors>
            <div class="dadata-input__error" v-if="$v.form.region.$error">
              <div v-if="!$v.form.region.required">Обязательное поле</div>
            </div>
          </template>
        </dadata-custom>

        <div class="form-group form-block_wrapping form-group_radio" style="order: 8">
          Пол*
          <radio-input
            label="Мужской"
            value="male"
            name="sex"
            tabindex="8"
            v-model="$v.form.sex.$model"
          />
          <radio-input
            label="Женский"
            value="female"
            name="sex"
            tabindex="7"
            v-model="$v.form.sex.$model"
          />
          <div v-if="$v.form.sex.$error" class="radio-input__error">Заполните поле</div>
        </div>

      </div>
      <div class="form-row form-block_wrapping form-row_mobile-no-flex">
        <check-input-personal-data-consent
          v-model="$v.form.personalDataConsent.$model"
          @change="$v.form.personalDataConsent.$touch()"
          :error="$v.form.personalDataConsent.$error"
          name="personalDataConsent"
        />
      </div>
      <div class="form-row form-row_mobile-no-flex">
        <check-input
          v-model="$v.form.newsletterConsent.$model"
          name="newsletterConsent"
        >
          Я даю согласие на&nbsp;получение информационной рассылки от&nbsp;ООО&nbsp;«Дж.Т.И.&nbsp;Россия», включая передачу коротких текстовых сообщений, в&nbsp;том числе всплывающих сообщений
        </check-input>
      </div>

      <div class="form-row">
        <div class="form__note form__note_icon_info">
          <p>Если Вы прервёте регистрацию, мы сохраним Ваши данные в&nbsp;течение 24&nbsp;часов. После этого они будут автоматически удалены, если&nbsp;вы не&nbsp;завершите регистрацию</p>
        </div>
      </div>

      <!--sign-input v-model="form.personalDataAgreementSignature"/-->
      <!--
      <div class="form-row">
        <div class="form__note">
          <p>Внимательно проверьте введенные данные.<br/> После регистрации Вы не сможете их изменить.</p>
        </div>
      </div>
      -->
      <smart-captcha ref="smartCaptcha" v-model="$v.form.captcha.$model">
        <template #errors>
          <div class="text-input__error">
            <div v-if="$v.form.captcha.$error">Необходимо пройти капчу</div>
          </div>
        </template>
      </smart-captcha>
      <div class="form-row form-row_mobile-wrap form__actions">
        <div class="form__nav">
          Уже зарегистрированы? <router-link v-if="!$store.state.currentUser" :to="{name: 'login'}" class="form__link">Войти</router-link>
          <!--
          <router-link :to="{ name: 'feedback' }" class="form__link">Написать нам</router-link>
          -->
        </div>
        <button class="form__submit button button__lg button__blue" type="submit">
          Продолжить
        </button>
      </div>

    </form>
    <div class="sign-up-error" v-else>
      <h2 class="form__title">Системная ошибка</h2>
      <p class="form__description form__description_align-center">Пожалуйста, повторите попытку позже, или обратитесь <br/>в&nbsp;службу поддержки.</p>
      <button class="form__submit button button__lg button__blue" @click="submit" type="submit">Далее</button>
    </div>
  </div>
</template>

<script>
import {required, helpers, minLength} from "vuelidate/lib/validators";
import CheckInput from "./CheckInput";
import RadioInput from './RadioInput';
import TextInput from './TextInput';
import SmartCaptcha from "./SmartCaptcha.vue";
import api from '../services/api';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/ru';
import 'vue2-datepicker/index.css';
import {VueMaskDirective} from "v-mask";
import resizeMixin from "../mixins/resizeMixin";
import { copyObject, isValidDate, isValidName, isValidUnicodeEmail, emailToPunycode } from "../utils.js";
import SmartCaptchaMixin from "../mixins/smartCaptchaMixin";

const adultBirthDate = (value) => {
  const minVal = new Date().setFullYear(new Date().getFullYear() - 18);
  const maxVal = new Date().setFullYear(new Date().getFullYear() - 99);
  return value > minVal || value < maxVal;
};

const availableDate = (value) => {
    if (helpers.req(value)) {
      if (!(value.length < 10)) {
        let formatDate = value.split('-');
        formatDate = [formatDate[1], formatDate[0], formatDate[2]];
        return !adultBirthDate(new Date(formatDate.join('-'))) &&
          !isNaN(new Date(new Date(formatDate.join('/'))).getTime());
      } else return false;
    } else return false;
};

export default {
  name: "SignUpStepData",
  mixins: [resizeMixin, SmartCaptchaMixin],
  components: {
    SmartCaptcha,
    RadioInput,
    CheckInput,
    TextInput,
    DatePicker,
  },
  directives: {
      mask: VueMaskDirective
  },
  data() {
    return {
      dateFocus: false,
      verifyPersonError: false,
      registerSuccess: false,
      birthDateInvalid: false, // birthDateInvalid принимает значение true, если строка даты правильная по формату (dd-mm-yyyy), но при этом такой даты не существует (30 февраля, 31 апреля); это дополнительное свойство нужно, так как vue2-datepicker не проводит такую проверку, он просто ставит 1 мая вместо 31 апреля или 2 марта вместо 30 февраля (если год невисокосный)
      form: {
        lastName: '',
        firstName: '',
        middleName: '',
        birthDate: '',
        mobilePhone: '',
        sex: '',
        email: '',
        location: '',
        region: '',
        //personalDataAgreementSignature: '',
        personalDataConsent: false,
        newsletterConsent: false,
        captcha: '',
        utm: {},
      },
      error: null,
    };
  },
  computed: {
    adultBirthDateInit() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 18);
      return date;
    },
    birthDatePopupClassname() {
      return this.birthDateInvalid ? 'birthdate-popup_hidden' : '';
    },
    errors() {
      return this.$store.state.errors;
    }
  },
  validations() {
    const form = {
      lastName: {required, isValidName},
      firstName: {required, isValidName},
      middleName: {required, isValidName},
      birthDate: {
        required,
        availableDate,
      },
      mobilePhone: {
        required,
        minLength: minLength(18)
      },
      email: {required, isValidUnicodeEmail},
      region: {required},
      sex: {required},
      personalDataAgreementSignature: {},
      personalDataConsent: { checked: value => value === true },
      newsletterConsent: {},
      captcha: {required}
    }
    return { form };
  },
  async mounted() {
    // Сбросить значение флага в сессии, чтобы использовать его на шаге 'document'
    await api.simplePostRequest('/api/setDelayedDocumentCheck', { delayedDocumentCheck: false});
  },
  methods: {
    async submit() {
      await this.refreshCaptcha();

      this.$v.form.$touch();

      if (!this.$v.form.$pending && !this.$v.form.$invalid) {
        this.$store.dispatch('showLoader');

        const params = this.getUrlParams();
        let utm = {}
        let flag = false
        for (let item in params) {
          if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].indexOf(item) !== -1) {
            utm[item] = params[item];
            flag = true;
          }
        }

        if (flag) {
          this.$set(this.form, 'utm', utm)
        }

        let result;
        const formCopy = copyObject(this.form);

        formCopy.email = emailToPunycode(formCopy.email);

        try {
          // Регистрируем пользователя в Битриксе и в Mindbox
          if (!this.registerSuccess) {
            // Исключаем поле 'region' из запроса
            delete formCopy.region;
            await api.registerStepData(formCopy);
            this.registerSuccess = true;
            // TODO обработать случай ошибки сохранения данных
          }

          this.$emit('complete', result, this.form.mobilePhone, formCopy.email, formCopy);
          this.$store.dispatch('hideErrorsBag');
        } catch (e) {
          const isPhoneAlreadyExist = e?.errors?.some(
            (error) => error?.code === 10
          );
          const phoneAlreadyExistText = "Введите другой мобильный телефон или <a href='/reset/'>восстановите пароль</a>"

          if (isPhoneAlreadyExist) {
            this.$store.dispatch("setErrors", {
              errors: [
                {
                  message: phoneAlreadyExistText,
                },
              ],
            });
          } else {
            this.$store.dispatch("setErrors", e);
          }
        } finally {
          this.$store.dispatch('hideLoader');
        }
      }
    },
    adultBirthDate(value) {
      return adultBirthDate(value);
    },
    getUrlParams() {
      const url = window.location
      let params = {};

      new URL(url).searchParams.forEach(function (val, key) {
        // Проверяем параметр на undefined
        if (params[key] !== undefined) {
          // Проверяем, имеется ли в объекте аналогичный params[key]
          // Если его нет, то добавляем его в объект
          if (!Array.isArray(params[key])) {
            params[key] = [params[key]];
          }
          params[key].push(val);
        } else {
          params[key] = val;
        }
      });
      return params;
    },
    handleRegionDadataSuggestion(dadataSuggestion) {
      this.form.location = dadataSuggestion.data.fias_id;
    },
    validateBirthDate(e) {
      const result = isValidDate(e.target.value);
      this.birthDateInvalid = result !== true && result.message === 'incorrect date';
    }
  }
}
</script>

<style lang="scss">
  @import "../scss/base/_settings.scss";
  .form {
    &__actions {
      margin-top: 32px;
    }
    &__description_align-center {
      @include mobile {
        text-align: left;
      }
    }
    &__nav {
      @include mobile {
        flex-direction: column;
        justify-content: center;
        gap: 6px;
        .form__link {
          margin-bottom: 0 !important;
        }
      }
    }
  }
  .birthdate-popup_hidden {
    opacity: 0;
    pointer-events: none;
  }
</style>
<style lang="scss" scoped>
@import "../scss/base/_settings.scss";
.sign-up-error {
  margin-top: -10px;
  margin-bottom: -15px;
  @include mobile {
    margin: 0;
  }
  .form__title {
    margin-bottom: 34px;
  }
  .form__description {
    font-size: 16px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
